import { useEffect, useRef, useState } from "react";
import { Box, InputLabel, Typography, useTheme } from "@mui/material";
import { useFormik } from "formik";
import useSWRMutation from "swr/mutation";
import ModalLayout from "../../../components/ModalLayout";
import CustomButton from "../../../components/CustomButton";
import CustomInput from "../../../components/CustomInput";
import CustomPasswordInput from "../../../components/CustomPasswordInput";
import CloseCircleSVG from "../../../../media/images/icons/close-icon";
import LoginIcon from "../../../../media/images/icons/login";
import { loginValidator } from "../../../../formValidators/auth/loginFormVlidators";
import { fetcherPost } from "../../../../services/fetcher";
import { useAppDispatch } from "../../../../hooks/useRedux";
import { handleUserLoginTabbar } from "../../../../redux/features/settingSlice";
import {
  setOpenSnackbar,
  setSnackbarDescription,
  setSnackbarErrorStatus,
} from "../../../../redux/features/snackbarSlice";

interface Props {
  open: boolean;
  onClose: () => void;
  onLoggedIn: () => void;
}

export default function DriverLoginModal({ open, onClose, onLoggedIn }: Props) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const [token, setToken] = useState("");
  const waitForRoleCheck = useRef(false);

  const { trigger: getUserInfoTrigger, isMutating: checkingRole } =
    useSWRMutation([`/User/GetUserInfo`, token], fetcherPost, {
      onSuccess: (data: { role?: number }) => {
        if (data?.role !== 12) {
          dispatch(setOpenSnackbar(true));
          dispatch(setSnackbarErrorStatus(400));
          dispatch(
            setSnackbarDescription("برای دریافت بار باید با حساب راننده وارد شوید.")
          );
          return;
        }

        localStorage.setItem("access_token", token);
        dispatch(handleUserLoginTabbar("drivers"));
        onLoggedIn();
      },
    });

  const { trigger: loginTrigger, isMutating: loggingIn } = useSWRMutation(
    [`/Account/Login`],
    fetcherPost,
    {
      onSuccess: (data: { access_token?: string }) => {
        if (!data?.access_token) return;
        waitForRoleCheck.current = true;
        setToken(data.access_token);
      },
      onError: (e: { response?: { data?: { errorCode?: number; error?: string } } }) => {
        dispatch(setOpenSnackbar(true));
        dispatch(setSnackbarErrorStatus(e.response?.data?.errorCode ?? 400));
        dispatch(
          setSnackbarDescription(
            e.response?.data?.error || "نام کاربری یا رمز عبور اشتباه است."
          )
        );
      },
    }
  );

  const formik = useFormik({
    initialValues: {
      userName: "",
      password: "",
    },
    validationSchema: loginValidator,
    onSubmit: (values) => {
      loginTrigger(values);
    },
  });

  useEffect(() => {
    if (waitForRoleCheck.current && token) {
      waitForRoleCheck.current = false;
      getUserInfoTrigger();
    }
  }, [token, getUserInfoTrigger]);

  useEffect(() => {
    if (!open) {
      formik.resetForm();
      setToken("");
      waitForRoleCheck.current = false;
    }
  }, [open]);

  return (
    <ModalLayout open={open} setOpen={() => onClose()}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
        <Typography
          sx={{ fontSize: "18px", fontWeight: 700, color: "primary.main", textAlign: "center" }}
        >
          ورود راننده
        </Typography>
        <Typography sx={{ fontSize: "13px", textAlign: "center", color: "textColor.main" }}>
          برای دریافت این بار، با حساب راننده وارد شوید. همین صفحه می‌مانید.
        </Typography>

        <Box>
          <InputLabel sx={{ color: "primary.main", fontWeight: 700, marginBottom: "8px" }}>
            نام کاربری راننده:
          </InputLabel>
          <CustomInput
            type="numeric"
            name="userName"
            handelChange={formik.handleChange}
            value={formik.values.userName}
            error={formik.touched.userName && Boolean(formik.errors.userName)}
            errorMessage={formik.touched.userName && formik.errors.userName}
          />
        </Box>

        <Box>
          <InputLabel sx={{ color: "primary.main", fontWeight: 700, marginBottom: "8px" }}>
            رمز عبور:
          </InputLabel>
          <CustomPasswordInput
            name="password"
            placHolder="رمز عبور خود را وارد کنید..."
            handelChange={formik.handleChange}
            value={formik.values.password}
            error={formik.touched.password && Boolean(formik.errors.password)}
            errorMessage={formik.touched.password && formik.errors.password}
          />
        </Box>

        <CustomButton
          loading={loggingIn || checkingRole}
          onClick={formik.handleSubmit}
          flexDirection="row"
          title="ورود و ادامه دریافت بار"
          justifyContent="center"
          bgColor="primary.main"
          width="100%"
          fSize="16px"
          fWeight="700"
          px="10px"
          py="10px"
          borderRadius="10px"
          textColor="lightStatic.main"
          icon2={<LoginIcon size={28} />}
        />
        <CustomButton
          type="outlined"
          rippleColor={theme.palette.primary.main}
          onClick={onClose}
          flexDirection="row-reverse"
          title="انصراف"
          justifyContent="center"
          bgColor={theme.palette.primary[50]}
          textColor="primary.main"
          borderColor="primary.main"
          fWeight="700"
          px="10px"
          py="10px"
          borderRadius="10px"
          icon1={<CloseCircleSVG size={22} color={theme.palette.primary.main} />}
        />
      </Box>
    </ModalLayout>
  );
}
